import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, CheckCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SessionContractPage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [error, setError] = useState(null);
  const [contractData, setContractData] = useState(null);
  const [signed, setSigned] = useState(false);

  const [typedName, setTypedName] = useState('');
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API_URL}/api/sessions/${token}/contract`);
        if (!res.ok) {
          const err = await res.json();
          setError(err.detail || 'Invalid or expired link');
          setLoading(false);
          return;
        }
        const data = await res.json();
        setContractData(data);
        if (data.signed) setSigned(true);
      } catch {
        setError('Unable to load contract. Please try again later.');
      }
      setLoading(false);
    };
    load();
  }, [token]);

  const handleSign = async () => {
    if (!typedName.trim()) { toast.error('Please type your full name'); return; }
    if (!agreed) { toast.error('Please agree to the terms'); return; }

    setSigning(true);
    try {
      const res = await fetch(`${API_URL}/api/sessions/${token}/sign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ typed_name: typedName, agreed: true }),
      });
      if (!res.ok) {
        const err = await res.json();
        toast.error(err.detail || 'Signing failed');
        return;
      }
      setSigned(true);
      toast.success('Contract signed successfully!');
    } catch {
      toast.error('Signing failed. Please try again.');
    } finally {
      setSigning(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-6">
      <Card className="max-w-md w-full text-center">
        <CardContent className="py-12">
          <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-800 mb-2">Link Unavailable</h2>
          <p className="text-slate-500">{error}</p>
        </CardContent>
      </Card>
    </div>
  );

  if (signed) return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-6">
      <Card className="max-w-md w-full text-center" data-testid="contract-signed">
        <CardContent className="py-12">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-8 w-8 text-emerald-600" />
          </div>
          <h2 className="text-xl font-bold text-slate-800 mb-2">Contract Signed</h2>
          <p className="text-slate-500">Thank you for signing the agreement. A copy will be sent to your email. Our team will be in touch shortly.</p>
          {contractData?.signature_name && (
            <p className="text-sm text-slate-400 mt-4">Signed by: {contractData.signature_name}</p>
          )}
        </CardContent>
      </Card>
    </div>
  );

  const d = contractData;
  const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  const renderTimeshareSummary = () => {
    if (!d.timeshares || d.timeshares.length === 0) {
      return <p className="ml-4 italic text-slate-400">No timeshare details provided</p>;
    }
    return d.timeshares.map((ts, i) => {
      const loc = [ts.city, ts.state].filter(Boolean).join(', ');
      return (
        <div key={ts.resort_name || `contract-ts-${i}`} className="ml-4 mb-3 p-3 bg-slate-50 rounded border border-slate-100">
          <p className="font-semibold text-slate-800">{ts.resort_name || 'Unknown Resort'}</p>
          {loc && <p className="text-slate-600">Location: {loc}</p>}
          <p className="text-slate-600">Type: {ts.type === 'points' ? 'Points' : 'Week'}</p>
          {ts.maintenance_fee && <p className="text-slate-600">Maintenance Fee: ${Number(ts.maintenance_fee).toLocaleString()}/yr</p>}
          {ts.has_balance && ts.balance_amount && <p className="text-rose-600">Outstanding Balance: ${Number(ts.balance_amount).toLocaleString()}</p>}
        </div>
      );
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-8 px-4" data-testid="session-contract-page">
      <div className="max-w-3xl mx-auto space-y-6">

        {/* ── PTC MASTER SERVICE AGREEMENT ── */}
        <Card className="overflow-hidden" data-testid="contract-document">
          <div className="bg-teal-700 px-8 py-6 text-center">
            <h1 className="text-xl font-bold text-white tracking-wide">PTC MASTER SERVICE AGREEMENT</h1>
            <p className="text-teal-200 text-sm mt-1">PRESTIGIOUS TRAVEL CLUB SERVICE AGREEMENT</p>
          </div>
          <CardContent className="px-8 py-8">
            <div className="text-slate-700 space-y-5 text-sm leading-relaxed">

              {/* Preamble */}
              <div>
                <p>This Service Agreement (&quot;Agreement&quot;) is entered into between:</p>
                <p className="mt-2 font-semibold text-slate-800">Prestigious Travel Club (&quot;PTC&quot;)</p>
                <p>and</p>
                <p className="font-semibold text-slate-800">{d.client_name} (&quot;Client&quot;)</p>
                <p className="mt-2">Effective Date: <strong>{today}</strong></p>
              </div>

              <hr className="border-slate-200" />

              {/* 1. SERVICES PROVIDED */}
              <div>
                <h3 className="text-base font-bold text-slate-800 mb-2">1. SERVICES PROVIDED</h3>
                <p>Prestigious Travel Club agrees to assist Client in the resolution, mitigation, or termination of Client&apos;s timeshare ownership obligations.</p>
                <p className="mt-2">PTC facilitates this process through internal processes and third-party partner firms when necessary.</p>
              </div>

              <hr className="border-slate-200" />

              {/* 2. CLIENT INFORMATION */}
              <div>
                <h3 className="text-base font-bold text-slate-800 mb-2">2. CLIENT INFORMATION</h3>
                <div className="space-y-1 ml-4">
                  <p>Client Name: <strong>{d.client_name}</strong></p>
                  <p>Address: <strong>{d.client_address || '--'}</strong></p>
                  <p>Email: <strong>{d.client_email}</strong></p>
                  <p>Phone: <strong>{d.client_phone || '--'}</strong></p>
                </div>
                <p className="mt-3 font-medium text-slate-800">Timeshare Details:</p>
                {renderTimeshareSummary()}
              </div>

              <hr className="border-slate-200" />

              {/* 3. SERVICE FEE */}
              <div>
                <h3 className="text-base font-bold text-slate-800 mb-2">3. SERVICE FEE</h3>
                <p>Client agrees to pay a total service fee of:</p>
                <p className="text-2xl font-bold text-slate-800 my-3 text-center">${Number(d.deal_amount || 0).toLocaleString()}</p>
                <p>This fee covers all services required to execute the agreed-upon strategy.</p>
              </div>

              <hr className="border-slate-200" />

              {/* 4. PAYMENT TERMS */}
              <div>
                <h3 className="text-base font-bold text-slate-800 mb-2">4. PAYMENT TERMS</h3>
                <p>Client may satisfy payment using one of the following methods:</p>
                <ul className="list-disc ml-8 mt-2 space-y-1">
                  <li>ACH (preferred)</li>
                  <li>Invoice (Bill.com)</li>
                  <li>Credit Card (processing fees may apply)</li>
                  <li>Third-party financing</li>
                </ul>
              </div>

              <hr className="border-slate-200" />

              {/* 5. FUNDING DISCLOSURE */}
              <div>
                <h3 className="text-base font-bold text-slate-800 mb-2">5. FUNDING DISCLOSURE</h3>
                <p>If Client elects to use third-party financing:</p>
                <ul className="list-disc ml-8 mt-2 space-y-1">
                  <li>Funds may be disbursed directly to Client</li>
                  <li>Client remains fully responsible for payment to Prestigious Travel Club</li>
                  <li>PTC is not dependent on lender disbursement</li>
                </ul>
              </div>

              <hr className="border-slate-200" />

              {/* 6. COLLECTION REQUIREMENT (CRITICAL) */}
              <div>
                <h3 className="text-base font-bold text-slate-800 mb-2">6. COLLECTION REQUIREMENT (CRITICAL)</h3>
                <p>Client acknowledges:</p>
                <p className="mt-2 font-semibold bg-amber-50 border border-amber-200 rounded p-3 text-amber-900">
                  A minimum of $1,400 must be collected before services are submitted to the partner firm.
                </p>
                <p className="mt-2">No processing or submission will occur until this threshold is met.</p>
              </div>

              <hr className="border-slate-200" />

              {/* 7. IN-HOUSE FINANCING (IF APPLICABLE) */}
              <div>
                <h3 className="text-base font-bold text-slate-800 mb-2">7. IN-HOUSE FINANCING (IF APPLICABLE)</h3>
                <p>If Client is approved for an internal payment plan:</p>
                <ul className="list-disc ml-8 mt-2 space-y-1">
                  <li>0% interest financing may be offered</li>
                  <li>A down payment is strongly encouraged</li>
                  <li>Payments must remain current</li>
                </ul>
                <p className="mt-3">Client acknowledges:</p>
                <ul className="list-disc ml-8 mt-2 space-y-1">
                  <li>Services will not proceed until the $1,400 threshold is met</li>
                  <li>Failure to maintain payments may result in suspension</li>
                </ul>
              </div>

              <hr className="border-slate-200" />

              {/* 8. SERVICE TIMELINE */}
              <div>
                <h3 className="text-base font-bold text-slate-800 mb-2">8. SERVICE TIMELINE</h3>
                <p>Client understands that:</p>
                <ul className="list-disc ml-8 mt-2 space-y-1">
                  <li>The process may take 12-18 months</li>
                  <li>Timeline varies based on ownership type, resort, and external factors</li>
                </ul>
              </div>

              <hr className="border-slate-200" />

              {/* 9. CLIENT RESPONSIBILITIES */}
              <div>
                <h3 className="text-base font-bold text-slate-800 mb-2">9. CLIENT RESPONSIBILITIES</h3>
                <p>Client agrees to:</p>
                <ul className="list-disc ml-8 mt-2 space-y-1">
                  <li>Provide accurate and complete information</li>
                  <li>Respond promptly to communication</li>
                  <li>Follow all required steps</li>
                </ul>
                <p className="mt-2">Failure to comply may delay or impact results.</p>
              </div>

              <hr className="border-slate-200" />

              {/* 10. NON-PAYMENT */}
              <div>
                <h3 className="text-base font-bold text-slate-800 mb-2">10. NON-PAYMENT</h3>
                <p>If Client fails to make required payments:</p>
                <ul className="list-disc ml-8 mt-2 space-y-1">
                  <li>Services may be paused or terminated</li>
                  <li>No further action will be taken until resolved</li>
                </ul>
              </div>

              <hr className="border-slate-200" />

              {/* 11. REFUND POLICY */}
              <div>
                <h3 className="text-base font-bold text-slate-800 mb-2">11. REFUND POLICY</h3>
                <p>If the partner firm is unable to secure cancellation:</p>
                <p className="mt-2">Client is eligible for a refund:</p>
                <p className="mt-1 font-semibold text-slate-800">Less a $399 administrative fee</p>
                <p className="mt-2">Administrative fee covers:</p>
                <ul className="list-disc ml-8 mt-2 space-y-1">
                  <li>Case setup</li>
                  <li>Document preparation</li>
                  <li>Administrative handling</li>
                  <li>Processing and coordination</li>
                </ul>
                <p className="mt-2">Refund requests prior to completion or outside of this condition will not be honored.</p>
              </div>

              <hr className="border-slate-200" />

              {/* 12. CONFIDENTIALITY & SECURITY CODE */}
              <div>
                <h3 className="text-base font-bold text-slate-800 mb-2">12. CONFIDENTIALITY &amp; SECURITY CODE</h3>
                <p>Client will be issued a unique Security Code.</p>
                <p className="mt-2">Client agrees:</p>
                <ul className="list-disc ml-8 mt-2 space-y-1">
                  <li>Not to disclose this code publicly</li>
                  <li>To verify this code when communicating with PTC</li>
                </ul>
                <p className="mt-2">PTC will not request sensitive information without proper verification.</p>
              </div>

              <hr className="border-slate-200" />

              {/* 13. LIMITATION OF LIABILITY */}
              <div>
                <h3 className="text-base font-bold text-slate-800 mb-2">13. LIMITATION OF LIABILITY</h3>
                <p>PTC acts as a facilitator and coordinator.</p>
                <p className="mt-2">PTC does not guarantee specific timelines or outcomes but commits to executing services professionally and in good faith.</p>
              </div>

              <hr className="border-slate-200" />

              {/* 14. ENTIRE AGREEMENT */}
              <div>
                <h3 className="text-base font-bold text-slate-800 mb-2">14. ENTIRE AGREEMENT</h3>
                <p>This Agreement represents the full understanding between parties and supersedes prior discussions.</p>
              </div>

              <hr className="border-slate-200" />

              {/* 15. SIGNATURE */}
              <div>
                <h3 className="text-base font-bold text-slate-800 mb-2">15. SIGNATURE</h3>
                <p>By signing below, Client acknowledges and agrees to all terms.</p>
              </div>

            </div>
          </CardContent>
        </Card>

        {/* ── Electronic Signature ── */}
        <Card className="border-2 border-teal-200" data-testid="signature-section">
          <CardHeader className="pb-3"><CardTitle className="text-base">Electronic Signature</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Type your full legal name *</Label>
              <Input
                value={typedName}
                onChange={e => setTypedName(e.target.value)}
                placeholder={d.client_name || 'Your full name'}
                className="text-lg h-12 mt-1"
                data-testid="signature-name"
              />
              {typedName && (
                <p className="mt-2 text-2xl font-script italic text-slate-700 border-b-2 border-slate-300 pb-1 px-2" data-testid="signature-preview">
                  {typedName}
                </p>
              )}
            </div>
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={agreed}
                onChange={e => setAgreed(e.target.checked)}
                className="rounded mt-0.5"
                id="agree-checkbox"
                data-testid="agree-checkbox"
              />
              <Label htmlFor="agree-checkbox" className="text-sm text-slate-600 cursor-pointer leading-relaxed">
                I have read and agree to the terms of this Service Agreement. I understand this constitutes a legally binding electronic signature.
              </Label>
            </div>
            <Button
              className="w-full bg-teal-600 hover:bg-teal-700 text-white h-12 text-base gap-2"
              onClick={handleSign}
              disabled={signing || !typedName.trim() || !agreed}
              data-testid="sign-contract-btn"
            >
              {signing ? <Loader2 className="h-5 w-5 animate-spin" /> : <CheckCircle className="h-5 w-5" />}
              {signing ? 'Signing...' : 'Sign Agreement'}
            </Button>
            <p className="text-[10px] text-center text-slate-400">
              Your IP address and timestamp will be recorded for verification purposes.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SessionContractPage;
